import type { PaletteDto } from '@miyuki/types';
import { Button, Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@miyuki/ui';

import { t } from '@/lib/i18n';

/**
 * Qué hacer con las mostacillas de un color que se está quitando.
 *
 * No alcanza con confirmar: hay que decidir a qué color pasan, o si quedan
 * vacías (RF-18). Por eso es un panel de elección y no un diálogo de sí/no.
 */
export interface ReassignColorSheetProps {
  readonly open: boolean;
  readonly palette: PaletteDto;
  readonly removingIndex: number | null;
  readonly beadCount: number;
  readonly onReassign: (target: number | null) => void;
  readonly onClose: () => void;
}

export function ReassignColorSheet({
  open,
  palette,
  removingIndex,
  beadCount,
  onReassign,
  onClose,
}: ReassignColorSheetProps) {
  if (removingIndex === null) return null;

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent side="bottom" className="pb-8">
        <SheetHeader>
          <SheetTitle>{t('reassignBeadsTitle')}</SheetTitle>
          <SheetDescription>{t('reassignBeadsBody', { n: beadCount })}</SheetDescription>
        </SheetHeader>

        <div className="flex flex-wrap items-center gap-2 px-4">
          {palette.colors.map((color, index) =>
            index === removingIndex ? null : (
              <button
                key={`${color}-${index}`}
                type="button"
                aria-label={`${t('palette')} ${index + 1}`}
                className="border-border size-11 shrink-0 rounded-full border-2"
                style={{ backgroundColor: color }}
                onClick={() => {
                  onReassign(index);
                }}
              />
            ),
          )}
        </div>

        <div className="flex gap-2 px-4 pb-2">
          <Button
            variant="outline"
            size="touch"
            className="flex-1"
            onClick={() => {
              onReassign(null);
            }}
          >
            {t('leaveEmpty')}
          </Button>
          <Button variant="ghost" size="touch" className="flex-1" onClick={onClose}>
            {t('cancel')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
